import { Fragment, useEffect, useState, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router'
import { ApiError, del, get, post, type Ingest, type StreamKey, type StreamKeyWithSecret } from '../api'
import { Copy, Row } from './ObsConnection'
import { mainKey, verticalOn } from './vertical'
import { t, type Key } from '../i18n'
import { BILLING_PATH, refusalText } from '../billing'
import { useSession } from '../session'
import { Button, Icon, Note, Tag } from '../ui'

/** `**x**` in a dictionary text is a menu item or the lead of a note: bold (design/vertical/texts.md). */
export function rich(text: string): ReactNode {
  const parts = text.split(/\*\*(.+?)\*\*/g)
  if (parts.length === 1) return text
  return parts.map((p, i) => (i % 2 ? <b key={i} className="font-medium text-text">{p}</b> : <Fragment key={i}>{p}</Fragment>))
}

/** A numbered instruction (DS: VT-06 — the system has no numbered list; the number box is `.sx-step .n`). */
export function HowTo({ steps, done = [], testId }: { steps: ReactNode[]; done?: number[]; testId?: string }) {
  return (
    <ol data-testid={testId} className="m-0 flex list-none flex-col gap-2.5 p-0">
      {steps.map((s, i) => (
        <li key={i} className="grid grid-cols-[20px_minmax(0,1fr)] gap-2.5 text-[13px] leading-[18px] text-text-soft">
          {/* DS: VT-06 — number in a 20 px box, a done step shows check */}
          <span aria-hidden className="grid h-5 w-5 place-items-center rounded-sm border border-line-strong font-mono text-[11px] font-medium text-text-soft">
            {done.includes(i) ? <Icon name="check" size={12} /> : i + 1}
          </span>
          <span>{s}</span>
        </li>
      ))}
    </ol>
  )
}

/** The encoders the vertical stream can come from, in the owner's order (VT-05, VT-15: no "next update" marks). */
const TABS: { id: string; label: Key; steps: Key[] }[] = [
  { id: 'plugin', label: 'vert.tab.plugin', steps: ['vert.plugin.1', 'vert.plugin.2', 'vert.plugin.3', 'vert.plugin.4'] },
  { id: 'bridge', label: 'vert.tab.bridge', steps: ['vert.bridge.1'] },
  { id: 'aitum', label: 'vert.tab.aitum', steps: ['vert.aitum.1', 'vert.aitum.2'] },
  { id: 'xsplit', label: 'vert.tab.xsplit', steps: ['vert.xsplit.1'] },
  { id: 'other', label: 'vert.tab.other', steps: ['vert.other.1'] },
]

/** Words for the center's codes of the vertical key (never the raw code). */
function errText(e: unknown, user: ReturnType<typeof useSession>['user']): { text: string; plan?: boolean } {
  if (e instanceof ApiError) {
    const plan = refusalText(e.code, user, e.data)
    if (plan) return { text: plan, plan: true }
    if (e.code === 'vertical_already_on' || /already/.test(e.message)) return { text: t('vert.err.alreadyOn') }
    if (e.code === 'key_not_main') return { text: t('vert.err.notMain') }
  }
  return { text: (e as Error).message }
}

/** Turn the vertical input on / off and issue a new vertical key (docs/vertical.md §1.2). */
export function useVerticalKey(streamName?: string) {
  const qc = useQueryClient()
  const { user } = useSession()
  const keys = useQuery({ queryKey: ['stream-keys'], queryFn: () => get<StreamKey[]>('/api/stream-keys') })
  const key = mainKey(keys.data, streamName)
  const [busy, setBusy] = useState<'on' | 'off' | 'rotate' | null>(null)
  const [err, setErr] = useState<{ text: string; plan?: boolean } | null>(null)
  /** the secret of the answer that turned it on or issued a new key: shown once, never cached */
  const [fresh, setFresh] = useState<string>()
  const run = async (what: 'on' | 'off' | 'rotate', call: () => Promise<StreamKeyWithSecret | StreamKey>) => {
    setBusy(what)
    setErr(null)
    try {
      const r = await call()
      await qc.invalidateQueries({ queryKey: ['stream-keys'] })
      await qc.invalidateQueries({ queryKey: ['destinations'] })
      // after the keys reload (a new id or state forgets every secret on screen): the answer's secret, once
      setFresh(what === 'off' ? undefined : (r as StreamKeyWithSecret).key)
      return true
    } catch (e) {
      setErr(errText(e, user))
      return false
    } finally {
      setBusy(null)
    }
  }
  return {
    key,
    loaded: !!keys.data,
    on: verticalOn(key),
    busy,
    err,
    fresh,
    forget: () => setFresh(undefined),
    turnOn: () => (key ? run('on', () => post<StreamKeyWithSecret>(`/api/stream-keys/${key.id}/vertical`)) : Promise.resolve(false)),
    turnOff: () => (key ? run('off', () => del<StreamKey>(`/api/stream-keys/${key.id}/vertical`)) : Promise.resolve(false)),
    rotate: () => (key?.vertical ? run('rotate', () => post<StreamKeyWithSecret>(`/api/stream-keys/${key.vertical!.id}/rotate`)) : Promise.resolve(false)),
  }
}

/**
 * «Vertical input» under the main key in OBS settings (mockup B1 / B2, VT-15): off — what it is and Turn on;
 * on — the server (the same as the main stream's), the vertical stream key (hidden: Show / Copy fetch it with
 * reveal; right after Turn on and New key the answer's secret shows once), how to send it from each encoder,
 * New key… (with a confirmation) and Turn off.
 */
export function VerticalInput({ streamName, ingest }: { streamName?: string; ingest?: Ingest }) {
  const v = useVerticalKey(streamName)
  const [tab, setTab] = useState(TABS[0]!.id)
  const [shown, setShown] = useState(false)
  const [secret, setSecret] = useState<string>()
  const [confirm, setConfirm] = useState(false)
  const vid = v.key?.vertical?.id
  // a new key, a turned-off input or another key: forget every secret on screen
  useEffect(() => { setSecret(undefined); setShown(false) }, [vid, v.on])
  useEffect(() => {
    if (v.fresh) { setSecret(v.fresh); setShown(true); v.forget() }
  }, [v.fresh]) // eslint-disable-line react-hooks/exhaustive-deps
  // a center before VERT-1 says nothing about the vertical input (no `vertical` on the key): no block
  if (!v.loaded || !v.key || v.key.revoked_at || v.key.vertical === undefined) return null
  const server = ingest?.rtmps ?? ingest?.rtmp
  const load = async () => {
    if (secret) return secret
    if (!vid) return undefined
    const k = await get<StreamKeyWithSecret>(`/api/stream-keys/${vid}/reveal`)
    setSecret(k.key)
    return k.key
  }
  const cur = TABS.find((x) => x.id === tab)!
  return (
    // DS: VT-15 — the block lives in OBS settings under the main key; a raised box with a header row
    <section data-testid="vertical-input" data-state={v.on ? 'on' : 'off'} aria-labelledby="vertical-input-title"
      className="flex flex-col rounded-lg border border-line-2 bg-bg">
      <div className="flex items-center gap-2 border-b border-line px-4 py-3">
        <Icon name="signal" size={16} className="text-text-soft" />
        <h3 id="vertical-input-title" className="m-0 text-ui-title font-medium">{t('vert.in.title')}</h3>
        <Tag>{t('vert.tag')}</Tag>
      </div>
      <div className="flex flex-col gap-3 px-4 pt-3 pb-3.5">
        <p className="m-0 text-[13px] leading-[18px] text-text-soft">{t('vert.in.desc')}</p>
        {!v.on ? (
          <>
            <p data-testid="vertical-off" className="m-0 text-[13px] leading-[18px] text-text-soft">{t('vert.in.off')}</p>
            <Button data-testid="vertical-on" busy={v.busy === 'on'} disabled={!!v.busy} onClick={() => void v.turnOn()} className="self-start max-md:min-h-[44px]">
              <Icon name="plus" size={16} />{t('vert.in.turnOn')}
            </Button>
            <span className="text-xs text-muted">{t('vert.in.turnOnHint')}</span>
          </>
        ) : (
          <>
            <div className="grid gap-x-4 gap-y-3 md:grid-cols-2">
              <Row label={t('vert.in.server')}>
                <code aria-label={t('vert.in.server')} className="v !block self-center truncate !text-xs">{server ?? '…'}</code>
                <Copy value={server} testId="vertical-server-copy" label={t('obs.copy' as Key)} />
              </Row>
              <Row label={t('vert.in.key')}>
                <code aria-label={t('vert.in.key')} data-testid="vertical-key" data-revealed={shown && secret ? 'true' : 'false'}
                  className={`v !block self-center truncate !text-xs ${shown && secret ? '' : 'dots'}`}>
                  {shown && secret ? secret : '•'.repeat(20)}
                </code>
                <button type="button" data-testid="vertical-key-show" className="flex-none max-md:min-h-[44px]"
                  onClick={async () => { if (!shown && !(await load())) return; setShown((x) => !x) }}>
                  <Icon name={shown ? 'eye-off' : 'eye'} size={16} />{shown ? t('common.hide') : t('common.show')}
                </button>
                <Copy onCopy={load} testId="vertical-key-copy" label={t('obs.copy' as Key)} />
              </Row>
            </div>
            <div className="flex flex-col gap-2">
              <span id="vertical-how" className="text-xs text-muted">{t('vert.in.how')}</span>
              {/* VT-05: tabs sized to their labels (.sx-tabs.fit), the owner's order */}
              <div role="tablist" aria-labelledby="vertical-how" className="sx-tabs fit">
                {TABS.map((x) => (
                  <a key={x.id} role="tab" href={`#vertical-${x.id}`} aria-selected={tab === x.id} data-testid={`vertical-tab-${x.id}`}
                    className={tab === x.id ? 'on' : undefined} onClick={(e) => { e.preventDefault(); setTab(x.id) }}>{t(x.label)}</a>
                ))}
              </div>
              <div role="tabpanel" id={`vertical-${cur.id}`} className="pt-1">
                <HowTo testId="vertical-steps" steps={cur.steps.map((k) => rich(t(k)))} />
              </div>
            </div>
            <Note data-testid="vertical-same">{rich(t('vert.in.same'))}</Note>
            {confirm ? (
              <div role="alertdialog" aria-label={t('vert.in.rotate.title')} data-testid="vertical-rotate-confirm" className="flex flex-col gap-2.5 rounded-md border border-fail/45 bg-fail/[.06] p-3">
                <span className="text-sm font-medium text-fail">{t('vert.in.rotate.title')}</span>
                <span className="text-[13px] text-text-soft">{t('vert.in.rotate.body')}</span>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => setConfirm(false)}>{t('common.cancel' as Key)}</Button>
                  <Button size="sm" variant="danger" data-testid="vertical-rotate-yes" busy={v.busy === 'rotate'} disabled={!!v.busy}
                    onClick={async () => { if (await v.rotate()) setConfirm(false) }}>{t('vert.in.rotate.yes')}</Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <Button data-testid="vertical-rotate" disabled={!!v.busy} onClick={() => setConfirm(true)} className="max-md:min-h-[44px]">
                  <Icon name="refresh" size={16} />{t('obs.newKey' as Key)}
                </Button>
                <span className="min-w-[200px] flex-1 text-xs text-muted">{t('vert.in.newKeyHint')}</span>
                <Button variant="ghost" data-testid="vertical-off-btn" busy={v.busy === 'off'} disabled={!!v.busy} onClick={() => void v.turnOff()} className="max-md:min-h-[44px]">
                  {t('vert.in.turnOff')}
                </Button>
              </div>
            )}
          </>
        )}
        {v.err && (
          <div role="alert" data-testid="vertical-error" className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-fail">
            <span className="inline-flex items-center gap-1"><Icon name="state-fail" size={12} />{v.err.text}</span>
            {v.err.plan && <Link to={BILLING_PATH} className="text-accent no-underline">{t('vert.f.plan.a')} →</Link>}
          </div>
        )}
      </div>
    </section>
  )
}
